import { forwardRef, type ReactNode } from 'react'
import { isString } from 'lodash-es'
import type { TypographyProps, StackProps } from '@mui/material'
import { Trans } from 'react-i18next'
import { isComponentOfKind } from 'lib/helpers/isComponentOfKind/isComponentOfKind'
import { getSlotProps } from './helpers/getSlotProps/getSlotProps'
import * as $ from './Card.styled'

type CardSlotProps = Omit<StackProps, 'children'>

export interface CardProps
  extends Partial<Pick<$.RootProps, 'id' | 'size' | 'corners' | 'margin' | 'sx' | 'fullWidth' | 'children'>> {
  'data-testid'?: string
  minHeight?: number
  slots?: {
    left?: ReactNode
    content: ReactNode
    /**
     * Competes with the content for the row and drops below it when the card is too narrow. Text
     * actions belong here — but not when the card also fills `actions`: the dropped button settles at
     * the trailing edge of the wrapping row, short of the pinned `actions` above it, and reads as
     * misaligned. Put it in `actions` beside them instead.
     */
    right?: ReactNode
    /**
     * Pinned to the trailing edge of the content's row, at any card width. Icon buttons and overflow
     * menus belong here, along with any text action that would otherwise sit in `right`.
     */
    actions?: ReactNode
  }
  slotProps?: {
    left?: CardSlotProps
    content?: TypographyProps
    right?: CardSlotProps
    actions?: CardSlotProps
    flexWrap?: CardSlotProps
  }
}

/**
 * The shell every message and inline panel is built from: a tinted, rounded box laying a leading
 * slot, the content and the trailing slots out on a single row.
 *
 * Prefer `MessageCard` for anything the user reads as a message — it adds the severity variants and
 * the matching leading icon on top of this. Reach for `Card` directly only for a shell that carries
 * neither.
 */
export const Card = forwardRef<HTMLDivElement, CardProps>(
  (
    {
      slots,
      slotProps,
      children,
      'data-testid': dataTestId,
      minHeight = 40,
      corners = 'rounded',
      size = 'medium',
      margin = 'normal',
      fullWidth = false,
      ...props
    },
    ref,
  ) => {
    const isContentTrans = isComponentOfKind(slots?.content, Trans)

    return (
      <$.Root
        corners={corners}
        ref={ref}
        data-testid={dataTestId}
        size={size}
        margin={margin}
        fullWidth={fullWidth}
        {...props}
      >
        {slots && (
          <$.SlotsWrapper size={size}>
            {slots.left && (
              <$.LeftSlot size={size} {...getSlotProps({ slotProps: slotProps?.left, minHeight })}>
                {slots.left}
              </$.LeftSlot>
            )}
            <$.FlexWrap size={size} {...slotProps?.flexWrap}>
              <$.ContentSlot
                size={size}
                variant="body2"
                component={isString(slots?.content) || isContentTrans ? 'p' : 'div'}
                {...getSlotProps({ slotProps: slotProps?.content, minHeight })}
              >
                {isContentTrans ? <span>{slots.content}</span> : slots.content}
              </$.ContentSlot>
              {slots.right && (
                <$.RightSlot size={size} {...getSlotProps({ slotProps: slotProps?.right, minHeight })}>
                  {slots.right}
                </$.RightSlot>
              )}
            </$.FlexWrap>
            {slots.actions && (
              <$.ActionsSlot size={size} {...getSlotProps({ slotProps: slotProps?.actions, minHeight })}>
                {slots.actions}
              </$.ActionsSlot>
            )}
          </$.SlotsWrapper>
        )}
        {children}
      </$.Root>
    )
  },
)

Card.displayName = 'Card'
